import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime, formatMoney, formatNumber } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  batchTone,
  getBatch,
  listAgents,
  listBatches,
  startBatch,
  stopBatch,
  type AgentBatch,
} from './api';
import { useRealtimeTopic } from '../app/realtime-store';

/**
 * Running an agent over many records (context.md sections 90, 97).
 *
 * The screen is a progress bar and a stop button, because that is what somebody wants from a job
 * that will take an hour: to see it moving, and to be able to change their mind.
 *
 * The money is shown next to the progress rather than tucked away in a report. An agent working
 * fourteen hundred records is the one thing in this product that can quietly become a bill, and a
 * number somebody can see while it happens is worth more than one they find afterwards.
 */

const ENTITIES = ['LEAD', 'CONTACT', 'COMPANY', 'OPPORTUNITY'] as const;

export function BatchesPage(): React.JSX.Element {
  const client = useQueryClient();

  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const batches = useQuery({
    queryKey: ['ai-batches'],
    queryFn: listBatches,
    /*
     * Pushed, with a slow floor.
     *
     * The runner announces each pass (Phase 80). The poll stays as a safety net, because a socket
     * that is silently down and a batch that is merely slow look identical from here.
     */
    refetchInterval: (query) =>
      (query.state.data?.batches ?? []).some(
        (batch) => batch.status === 'PENDING' || batch.status === 'RUNNING',
      )
        ? 30_000
        : false,
  });

  useRealtimeTopic('ai_batch', () => {
    void client.invalidateQueries({ queryKey: ['ai-batches'] });

    // The open batch, if somebody is reading one: the same event carries its id.
    if (open) void client.invalidateQueries({ queryKey: ['ai-batch', open] });
  });

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['ai-batches'] });
  };

  const stop = useMutation({
    mutationFn: (id: string) => stopBatch(id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const rows = batches.data?.batches ?? [];
  const selected = rows.find((batch) => batch.id === open) ?? null;

  const columns: Column<AgentBatch>[] = [
    {
      key: 'batch',
      header: t('ai.batch'),
      text: (batch) => `${batch.name} ${batch.agentName} ${t(`ai.entity.${batch.entityType}`)}`,
      render: (batch) => (
        <span className="cell-stack">
          <span className="strong">{batch.name}</span>
          <span className="muted small">
            {batch.agentName} · {t(`ai.entity.${batch.entityType}`)}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      text: (batch) => `${t(`ai.batch.${batch.status}`)} ${batch.failureReason ?? ''}`.trim(),
      render: (batch) => (
        <span className="cell-stack">
          <Badge tone={batchTone(batch.status)}>{t(`ai.batch.${batch.status}`)}</Badge>
          {batch.failureReason && <span className="muted small">{batch.failureReason}</span>}
        </span>
      ),
    },
    {
      key: 'progress',
      header: t('ai.batchProgress'),
      text: (batch) => progressText(batch),
      render: (batch) => (
        <span className="health">
          <span className="health__track">
            <span
              className="health__fill health__fill--watch"
              style={{ width: `${(batch.progress.completion ?? 0) * 100}%` }}
            />
          </span>
          <span className="muted small">
            {t('ai.batchCounts')
              .replace('{done}', formatNumber(batch.progress.done))
              .replace('{total}', formatNumber(batch.progress.total))}
            {batch.progress.failed > 0 &&
              ` · ${t('ai.batchFailed').replace('{n}', String(batch.progress.failed))}`}
          </span>
        </span>
      ),
    },
    {
      key: 'spent',
      header: t('ai.batchSpent'),
      align: 'end',
      text: (batch) => formatMoney(batch.progress.spentCents),
      render: (batch) => (
        <span className="cell-stack">
          <span>{formatMoney(batch.progress.spentCents)}</span>
          {batch.budgetCents !== null && (
            <span className="muted small">
              {t('ai.batchBudget').replace('{value}', formatMoney(batch.budgetCents))}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'created',
      header: t('common.createdAt'),
      text: (batch) => formatDateTime(batch.createdAt),
      render: (batch) => <span className="muted small">{formatDateTime(batch.createdAt)}</span>,
    },
    // Off by default; the chooser turns them on and the account remembers the choice.
    {
      key: 'agent',
      header: t('ai.agent'),
      hidden: true,
      text: (batch) => batch.agentName,
      render: (batch) => batch.agentName,
    },
    {
      key: 'entity',
      header: t('ai.batchEntity'),
      hidden: true,
      text: (batch) => t(`ai.entity.${batch.entityType}`),
      render: (batch) => t(`ai.entity.${batch.entityType}`),
    },
    {
      key: 'started',
      header: t('automations.startedAt'),
      hidden: true,
      text: (batch) => (batch.startedAt ? formatDateTime(batch.startedAt) : ''),
      render: (batch) => (batch.startedAt ? formatDateTime(batch.startedAt) : '—'),
    },
    {
      key: 'finished',
      header: t('automations.finishedAt'),
      hidden: true,
      text: (batch) => (batch.finishedAt ? formatDateTime(batch.finishedAt) : ''),
      render: (batch) => (batch.finishedAt ? formatDateTime(batch.finishedAt) : '—'),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (batch) =>
        batch.status === 'PENDING' || batch.status === 'RUNNING' ? (
          <button
            type="button"
            className="button button--quiet button--compact"
            disabled={stop.isPending}
            onClick={(event) => {
              // The row opens the batch; the button must not do both.
              event.stopPropagation();
              stop.mutate(batch.id);
            }}
          >
            {t('ai.stopBatch')}
          </button>
        ) : null,
    },
  ];

  return (
    <div className="view view--wide">
      <PageHeader
        title={t('ai.batches')}
        description={t('ai.batchesDescription')}
        actions={
          <button
            type="button"
            className="button button--primary button--compact"
            onClick={() => setCreating((current) => !current)}
          >
            {creating ? t('confirm.cancel') : t('ai.newBatch')}
          </button>
        }
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {creating && (
        <NewBatch
          onDone={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}

      <Panel>
        <DataTable
          tableKey="ai-batches"
          rows={batches.data?.batches}
          columns={columns}
          rowKey={(batch) => batch.id}
          isLoading={batches.isLoading}
          error={batches.error}
          onRetry={() => void batches.refetch()}
          onRowClick={(batch) => setOpen((current) => (current === batch.id ? null : batch.id))}
          empty={{ title: t('ai.noBatches') }}
        />
      </Panel>

      {/*
       * What the agent said, under the table rather than inside it.
       *
       * The list is paged and filtered now, so a row that expands in place would disappear under the
       * person mid-read the moment either changes. A panel of its own outlives the row.
       */}
      {selected && (
        <Panel title={selected.name}>
          <Items batchId={selected.id} />
        </Panel>
      )}
    </div>
  );
}

/** What the quick filter matches on the progress cell: the counts somebody can actually read. */
function progressText(batch: AgentBatch): string {
  const counts = t('ai.batchCounts')
    .replace('{done}', formatNumber(batch.progress.done))
    .replace('{total}', formatNumber(batch.progress.total));

  return batch.progress.failed > 0
    ? `${counts} ${t('ai.batchFailed').replace('{n}', String(batch.progress.failed))}`
    : counts;
}

/** What the agent said about each record, which is the only way to judge whether it worked. */
function Items({ batchId }: { batchId: string }): React.JSX.Element {
  const detail = useQuery({ queryKey: ['ai-batch', batchId], queryFn: () => getBatch(batchId) });

  if (detail.isLoading) return <Loading />;
  if (detail.error) {
    return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  }

  const items = detail.data?.items ?? [];

  if (items.length === 0) return <p className="muted small">{t('ai.batchNoItems')}</p>;

  return (
    <ul className="hits">
      {items.map((item) => (
        <li key={item.id} className="hit">
          <div className="hit__head">
            <span className="strong small">{t(`ai.item.${item.status}`)}</span>
            <span className="muted small">
              {item.finishedAt ? formatDateTime(item.finishedAt) : '—'}
            </span>
          </div>
          <p className="hit__body">{item.output ?? item.failureReason ?? '—'}</p>
        </li>
      ))}
    </ul>
  );
}

/**
 * Starting one.
 *
 * The budget is offered on the form rather than buried in settings, because the moment somebody is
 * about to run an agent over their whole database is the moment they should decide what that may
 * cost. Left empty it runs to the end, which is the right default for a hundred records and the
 * wrong one for a hundred thousand — so the field is there, in front of them.
 */
function NewBatch({ onDone }: { onDone: () => void }): React.JSX.Element {
  const [agentId, setAgentId] = useState('');
  const [name, setName] = useState('');
  const [instruction, setInstruction] = useState('');
  const [entityType, setEntityType] = useState<string>('LEAD');
  const [budget, setBudget] = useState('');
  const [error, setError] = useState<string | null>(null);

  const agents = useQuery({ queryKey: ['agents'], queryFn: () => listAgents(), staleTime: 60_000 });

  const start = useMutation({
    mutationFn: () =>
      startBatch({
        agentId,
        name: name.trim(),
        instruction: instruction.trim(),
        entityType,
        ...(budget.trim()
          ? { budgetCents: Math.round(Number(budget.replace(',', '.')) * 100) }
          : {}),
      }),
    onSuccess: () => {
      setError(null);
      setName('');
      setInstruction('');
      onDone();
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const active = (agents.data?.agents ?? []).filter((agent) => agent.status === 'ACTIVE');

  return (
    <Panel title={t('ai.newBatch')}>
      <div className="form-grid">
        <label className="stack__field">
          <span>
            {t('ai.agent')} <span className="required">*</span>
          </span>
          <select
            className="select"
            value={agentId}
            onChange={(event) => setAgentId(event.target.value)}
          >
            <option value="">{t('ai.pickAgent')}</option>
            {active.map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </select>
          {active.length === 0 && <span className="muted small">{t('ai.noPublishedAgent')}</span>}
        </label>

        <label className="stack__field">
          <span>{t('ai.batchEntity')}</span>
          <select
            className="select"
            value={entityType}
            onChange={(event) => setEntityType(event.target.value)}
          >
            {ENTITIES.map((entity) => (
              <option key={entity} value={entity}>
                {t(`ai.entity.${entity}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="stack__field">
        <span>
          {t('ai.batchName')} <span className="required">*</span>
        </span>
        <input value={name} maxLength={200} onChange={(event) => setName(event.target.value)} />
      </label>

      <label className="stack__field">
        <span>
          {t('ai.batchInstruction')} <span className="required">*</span>
        </span>
        <textarea
          rows={4}
          value={instruction}
          placeholder={t('ai.batchInstructionPlaceholder')}
          onChange={(event) => setInstruction(event.target.value)}
        />
        <span className="muted small">{t('ai.batchInstructionHint')}</span>
      </label>

      <label className="stack__field">
        <span>{t('ai.batchBudgetField')}</span>
        <input
          inputMode="decimal"
          value={budget}
          placeholder={t('ai.batchBudgetPlaceholder')}
          onChange={(event) => setBudget(event.target.value)}
        />
      </label>

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}

      <div className="confirm__actions">
        <button
          type="button"
          className="button button--primary button--compact"
          disabled={
            agentId === '' || name.trim() === '' || instruction.trim() === '' || start.isPending
          }
          onClick={() => start.mutate()}
        >
          {start.isPending ? t('work.creating') : t('ai.runBatch')}
        </button>
      </div>
    </Panel>
  );
}
